/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B000160 発注番号照会
 */
import { Column, Query } from "@material-table/core";
import { Box } from "@mui/material";
import {
  B000160Find,
  B000160FindRequest,
  B000170ShireNmr,
  B000190SokoNm,
  B000200JanShohinNmr,
  B000200ShohinNmr,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCodeSelect,
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import {
  B000160KanryoKbn,
  B000160ShohinshubetsuKbn,
  B000160Shutsuryokujun,
  getSCode,
  SCode,
} from "messages/codes";
import { labels } from "messages/labels";
import {
  B000120,
  B000130,
  B000140,
  B000170,
  B000190,
  B000200,
  B000202,
} from "pages";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { eigyoKengen } from "services/codes";
import { date, dateWithSlashAt } from "services/DateService";
import { authState, focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  eigyosyoKengen: string;
  gamenId: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  hachuYmdFrom: string;
  hachuYmdTo: string;
  nyukayoteiYmdFrom: string;
  nyukayoteiYmdTo: string;
  eigyoCdFrom: string;
  eigyoCdTo: string;
  bumonCdFrom: string;
  bumonCdTo: string;
  shainCdFrom: string;
  shainCdTo: string;
  hachudenpyoNoFrom: string;
  hachudenpyoNoTo: string;
  shireCd: string;
  shireNmr: string;
  sokoCd: string;
  sokoNm: string;
  shodaibun: string;
  shochubun: string;
  shoshobun: string;
  shohinCd: string;
  shohinNmr: string;
  kanryoKbn: SCode;
  janCd: string;
  janShohinNmr: string;
  shutsuryokujun: SCode;
  shohinshubetsuKbn: SCode;
  eigyosyoKengen: string;
  gamenId: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  uchiwakeNmr: string;
  hachuYmd: string;
  nyukayoteiYmd: string;
  hachudenpyoNo: number;
  hachudenpyoNoF: number;
  shohinCd: string;
  shohinNm: string;
  hachusu: number;
  shiresu: number;
  hachuzansu: number;
  hachuKin: number;
  hachuzanKin: number;
  sokoCd: string;
  sokoNm: string;
  shireNm: string;
  shainNm: string;
  bumonNmr: string;
  kanryoKbnView: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  hachuYmdFrom: date(),
  hachuYmdTo: date(),
  nyukayoteiYmdFrom: "",
  nyukayoteiYmdTo: "",
  eigyoCdFrom: "",
  eigyoCdTo: "",
  bumonCdFrom: "",
  bumonCdTo: "",
  shainCdFrom: "",
  shainCdTo: "",
  hachudenpyoNoFrom: "",
  hachudenpyoNoTo: "",
  shireCd: "",
  shireNmr: "",
  sokoCd: "",
  sokoNm: "",
  shodaibun: "",
  shochubun: "",
  shoshobun: "",
  shohinCd: "",
  shohinNmr: "",
  kanryoKbn: getSCode(B000160KanryoKbn, "0"),
  janCd: "",
  janShohinNmr: "",
  shutsuryokujun: getSCode(B000160Shutsuryokujun, "0"),
  shohinshubetsuKbn: getSCode(B000160ShohinshubetsuKbn, "0"),
  eigyosyoKengen: "",
  gamenId: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "4rem", align: "right" },
  { field: "uchiwakeNmr", title: "区分", width: "5rem" },
  {
    field: "hachuYmd",
    title: "発注日",
    width: "6rem",
    render: (data) => {
      if (data.hachuYmd! !== null && data.hachuYmd! !== "") {
        return dateWithSlashAt(data.hachuYmd!);
      }
    },
  },
  {
    field: "nyukayoteiYmd",
    title: "入荷予定日",
    width: "6rem",
    render: (data) => {
      if (data.nyukayoteiYmd! !== null && data.nyukayoteiYmd! !== "") {
        return dateWithSlashAt(data.nyukayoteiYmd!);
      }
    },
  },
  { field: "hachudenpyoNoF", title: "発注No.", width: "8rem" },
  { field: "shohinCd", title: "商品コード", width: "8rem" },
  { field: "shohinNm", title: "商品名", width: "20rem" },
  {
    field: "hachusu",
    title: "発注数　",
    width: "6rem",
    align: "right",
    render: (data) => data.hachusu?.toLocaleString(),
  },
  {
    field: "shiresu",
    title: "入荷数　",
    width: "6rem",
    align: "right",
    render: (data) => data.shiresu?.toLocaleString(),
  },
  {
    field: "hachuzansu",
    title: "発注残数　",
    width: "6rem",
    align: "right",
    render: (data) => data.hachuzansu?.toLocaleString(),
  },
  {
    field: "hachuKin",
    title: "発注金額　",
    width: "6rem",
    align: "right",
    render: (data) => data.hachuKin?.toLocaleString(),
  },
  {
    field: "hachuzanKin",
    title: "発注残金額　",
    width: "6rem",
    align: "right",
    render: (data) => data.hachuzanKin?.toLocaleString(),
  },
  { field: "sokoCd", title: "倉庫コード", width: "5rem" },
  { field: "sokoNm", title: "倉庫名", width: "10rem" },
  { field: "shireNm", title: "仕入先", width: "15rem" },
  { field: "shainNm", title: "発注担当者", width: "8rem" },
  { field: "bumonNmr", title: "発注担当部門", width: "6rem" },
  { field: "kanryoKbnView", title: "完了", width: "4rem" },
  { field: "hachudenpyoNo", hidden: true },
  { field: "shireCd", hidden: true },
];

const focusId = "B000160";

// B000160 発注番号照会
export const B000160 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログの開閉を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // 画面遷移
  const navigate = useNavigate();
  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // ログインユーザー情報
  const auth = useRecoilValue(authState);
  // 所属営業所コードでの営業所コード入力制御
  const [isEigyoCdHonsha, setIsEigyoCdHonsha] = useState(true);

  // 初期表示イベント
  useEffect(() => {
    // 営業所権限処理
    if (props.eigyosyoKengen) {
      methods.setValue("eigyosyoKengen", props.eigyosyoKengen);
    }
    // 画面ID処理
    if (props.gamenId) {
      methods.setValue("gamenId", props.gamenId);
    }

    // フォームのデータを取得する。
    const fields = methods.getValues();

    // 自営業所の設定
    // イベント利用権限定義マスタ.営業所権限が"0"(所属営業所のみ指定可)の場合、
    if (fields.eigyosyoKengen === eigyoKengen.only) {
      // 自営業所コード(変更不可)の設定
      methods.setValue("eigyoCdFrom", auth?.eigyoCd!);
      methods.setValue("eigyoCdTo", auth?.eigyoCd!);
      // 入力部非活性化
      setIsEigyoCdHonsha(false);
    }
    // 利用権限定義マスタ.営業所権限が"1"(全営業所指定可権限)の場合、
    else if (fields.eigyosyoKengen === eigyoKengen.all) {
      // 空白(入力可能)
      setIsEigyoCdHonsha(true);
    }

    // 初期表示
    focus(1);
    return;
  }, []);

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }

      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 完了区分
      let paramKanryoKbn;
      // 未完了
      if (fields.kanryoKbn?.value! === "0") {
        paramKanryoKbn = fields.kanryoKbn?.value!;
        // 全件
      } else if (fields.kanryoKbn?.value! === "1") {
        paramKanryoKbn = "2";
      }

      // 出力順に応じたソート処理
      let order;
      if (fields.shutsuryokujun?.value === "0") {
        order = ["hachuYmd,asc", "hachudenpyoNo,asc", "hachugyoNo,asc"];
      } else if (fields.shutsuryokujun?.value === "1") {
        order = ["nyukayoteiYmd,asc", "hachudenpyoNo,asc", "hachugyoNo,asc"];
      } else if (fields.shutsuryokujun?.value === "2") {
        order = ["shohinCd,asc", "hachudenpyoNo,asc", "hachugyoNo,asc"];
      } else if (fields.shutsuryokujun?.value === "3") {
        order = ["shireCd,asc", "hachudenpyoNo,asc", "hachugyoNo,asc"];
      } else if (fields.shutsuryokujun?.value === "4") {
        order = ["sokoCd,asc", "hachudenpyoNo,asc", "hachugyoNo,asc"];
      } else if (fields.shutsuryokujun?.value === "5") {
        order = ["hachudenpyoNo,asc", "hachugyoNo,asc"];
      }

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B000160FindRequest = {
        hachuYmdFrom: fields.hachuYmdFrom,
        hachuYmdTo: fields.hachuYmdTo,
        nyukayoteiYmdFrom: fields.nyukayoteiYmdFrom,
        nyukayoteiYmdTo: fields.nyukayoteiYmdTo,
        eigyoCdFrom: fields.eigyoCdFrom,
        eigyoCdTo: fields.eigyoCdTo,
        bumonCdFrom: fields.bumonCdFrom,
        bumonCdTo: fields.bumonCdTo,
        shainCdFrom: fields.shainCdFrom,
        shainCdTo: fields.shainCdTo,
        hachudenpyoNoFrom: fields.hachudenpyoNoFrom,
        hachudenpyoNoTo: fields.hachudenpyoNoTo,
        shireCd: fields.shireCd,
        sokoCd: fields.sokoCd,
        shodaibun: fields.shodaibun,
        shochubun: fields.shochubun,
        shoshobun: fields.shoshobun,
        shohinCd: fields.shohinCd,
        kanryoKbn: paramKanryoKbn,
        janCd: fields.janCd,
        gamenId: fields.gamenId,
        shohinshubetsuKbn: fields.shohinshubetsuKbn?.value,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: order,
      };
      // API呼出し
      B000160Find(param)
        // 正常値取得
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="発注番号照会"
        gridTemplateColumns="7rem 4rem 4rem 2rem 1rem 2rem 8rem 5rem 7rem 10rem 1rem 10rem 5rem 2rem"
      >
        {/* 1列目 */}
        <SLabel>発注日</SLabel>
        <SDateField name="hachuYmdFrom" gridColumn="span 3" {...register(1)} />
        <SText>～</SText>
        <SDateField name="hachuYmdTo" gridColumn="span 2" {...register(2)} />

        <Box />

        <SLabel>入荷予定日</SLabel>
        <SDateField name="nyukayoteiYmdFrom" {...register(3)} />
        <SText>～</SText>
        <SDateField name="nyukayoteiYmdTo" {...register(4)} />

        <Box gridColumn="span 2" />

        {/* 2列目 */}
        <SLabel>営業所</SLabel>
        {isEigyoCdHonsha ? (
          <STextField
            name="eigyoCdFrom"
            code
            size={6}
            gridColumn="span 3"
            dialog={<B000120 />}
            handleSubmit={(data: any) => {
              methods.setValue("eigyoCdFrom", data.eigyoCd);
            }}
            {...register(5)}
          />
        ) : (
          <STextField
            label="営業所コード（開始）"
            name="eigyoCdFrom"
            disabled
          />
        )}
        <SText>～</SText>
        {isEigyoCdHonsha ? (
          <STextField
            name="eigyoCdTo"
            code
            size={6}
            gridColumn="span 2"
            dialog={<B000120 />}
            handleSubmit={(data: any) => {
              methods.setValue("eigyoCdTo", data.eigyoCd);
            }}
            {...register(6)}
          />
        ) : (
          <STextField label="営業所コード（終了）" name="eigyoCdTo" disabled />
        )}

        <Box gridColumn="span 7" />

        {/* 3列目 */}
        <SLabel>発注担当部門</SLabel>
        <SNumberField
          name="bumonCdFrom"
          code
          size={6}
          gridColumn="span 3"
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdFrom", data.bumonCd);
          }}
          {...register(7)}
        />
        <SText>～</SText>
        <SNumberField
          name="bumonCdTo"
          code
          size={6}
          gridColumn="span 2"
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdTo", data.bumonCd);
          }}
          {...register(8)}
        />

        <Box />

        <SLabel>発注担当者</SLabel>
        <SNumberField
          name="shainCdFrom"
          code
          size={7}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdFrom", data.shainCd);
          }}
          {...register(9)}
        />
        <SText>～</SText>
        <SNumberField
          name="shainCdTo"
          code
          size={7}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdTo", data.shainCd);
          }}
          {...register(10)}
        />

        <Box gridColumn="span 2" />

        {/* 4列目 */}
        <SLabel>発注No.</SLabel>
        <SNumberField
          name="hachudenpyoNoFrom"
          code
          size={10}
          gridColumn="span 3"
          {...register(11)}
        />
        <SText>～</SText>
        <SNumberField
          name="hachudenpyoNoTo"
          code
          size={10}
          gridColumn="span 2"
          {...register(12)}
        />

        <Box gridColumn="span 7" />

        {/* 5列目 */}
        <SLabel>仕入先</SLabel>
        <SNumberField
          name="shireCd"
          code
          size={6}
          gridColumn="span 3"
          onBlur={() => B000170ShireNmr(methods, navigate, setMsg)}
          dialog={<B000170 />}
          handleSubmit={(data: any) => {
            methods.setValue("shireCd", data.shireCd);
            methods.setValue("shireNmr", data.shireNmr);
          }}
          {...register(13)}
        />
        <STextField name="shireNmr" size={24} disabled gridColumn="span 3" />

        <Box />

        <SLabel>倉庫</SLabel>
        <SNumberField
          name="sokoCd"
          code
          size={3}
          onBlur={() => B000190SokoNm(methods, navigate, setMsg)}
          dialog={<B000190 />}
          handleSubmit={(data: any) => {
            methods.setValue("sokoCd", data?.sokoCd);
            methods.setValue("sokoNm", data?.sokoNm);
          }}
          {...register(14)}
        />
        <STextField name="sokoNm" size={20} disabled gridColumn="span 4" />

        {/* 6列目 */}
        <SLabel>商品分類</SLabel>
        <SNumberField name="shodaibun" code size={3} {...register(15)} />
        <SNumberField name="shochubun" code size={3} {...register(16)} />
        <SNumberField
          name="shoshobun"
          code
          size={3}
          gridColumn="span 3"
          dialog={<B000202 />}
          handleSubmit={(data: any) => {
            if (data.shochubun === null) {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", "");
              methods.setValue("shoshobun", "");
            } else if (data.shoshobun === null) {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", data.shochubun);
              methods.setValue("shoshobun", "");
            } else {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", data.shochubun);
              methods.setValue("shoshobun", data.shoshobun);
            }
          }}
          {...register(17)}
        />

        <Box gridColumn="span 2" />

        <SLabel>商品</SLabel>
        <SNumberField
          name="shohinCd"
          size={10}
          onBlur={() => B000200ShohinNmr(methods, navigate, setMsg)}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("shohinCd", data.shohinCd);
            methods.setValue("shohinNmr", data.shohinNmr);
          }}
          {...register(18)}
        />
        <STextField name="shohinNmr" size={24} disabled gridColumn="span 4" />

        {/* 7列目 */}
        <SLabel>完了</SLabel>
        <SCodeSelect
          name="kanryoKbn"
          options={B000160KanryoKbn}
          gridColumn="span 3"
          {...register(19)}
        />

        <Box gridColumn="span 4" />

        <SLabel>JANコード</SLabel>
        <SNumberField
          name="janCd"
          code
          size={13}
          onBlur={() => B000200JanShohinNmr(methods, navigate, setMsg)}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("janCd", data.janCd);
            methods.setValue("janShohinNmr", data.shohinNmr);
          }}
          {...register(20)}
        />
        <STextField
          name="janShohinNmr"
          size={24}
          disabled
          gridColumn="span 4"
        />

        {/* 8列目 */}
        <SLabel>出力順</SLabel>
        <SCodeSelect
          name="shutsuryokujun"
          options={B000160Shutsuryokujun}
          gridColumn="span 3"
          {...register(21)}
        />

        <Box gridColumn="span 4" />

        <SLabel>商品種別</SLabel>
        <SCodeSelect
          name="shohinshubetsuKbn"
          options={B000160ShohinshubetsuKbn}
          gridColumn="span 2"
          {...register(22)}
        />

        <Box gridColumn="span 3" />

        <Box gridColumn="span 13" />

        <SNormalButton
          label={labels.buttons.search}
          align="right"
          onClick={handleClickSearchButton}
          {...register(23)}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
          width={1100}
        />
      </SFrame>
    </FormProvider>
  );
};
